import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Search, Loader2, Mail, Users, X, Paperclip, Building2, MapPin, RotateCcw, Send,
} from "lucide-react";

const SEG_COLORS = ["bg-signal-500", "bg-emerald-500", "bg-violet-500", "bg-amber-500", "bg-sky-500", "bg-rose-500"];
const segColor = (i) => SEG_COLORS[i % SEG_COLORS.length];
const prettySeg = (s) => (s || "unknown").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function ContactsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [segment, setSegment] = useState("all");
  const [country, setCountry] = useState("all");
  const [facets, setFacets] = useState({ total: 0, segments: [], countries: [] });
  const [selected, setSelected] = useState([]);
  const [composing, setComposing] = useState(false);

  const load = useCallback(async (offset = 0) => {
    setLoading(true);
    try {
      const { data } = await api.get("/contacts", { params: { q: q || undefined, segment, country, limit: 50, offset } });
      setItems(offset === 0 ? data.items : (prev) => [...prev, ...data.items]);
      setTotal(data.total || 0);
      setHasMore(!!data.has_more);
      if (offset === 0) setSelected([]);
    } catch { toast.error("Could not load contacts"); }
    setLoading(false);
  }, [q, segment, country]);

  useEffect(() => { (async () => {
    try { const { data } = await api.get("/contacts/facets"); setFacets(data); } catch { /* */ }
  })(); }, []);

  useEffect(() => { const t = setTimeout(() => load(0), 300); return () => clearTimeout(t); }, [load]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allOnPage = items.length > 0 && items.every((p) => selected.includes(p.id));
  const toggleAll = () => setSelected(allOnPage ? selected.filter((id) => !items.some((p) => p.id === id)) : [...new Set([...selected, ...items.map((p) => p.id)])]);
  const clearFilters = () => { setQ(""); setSegment("all"); setCountry("all"); };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="contacts-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold tracking-tight">Contacts</h1>
            <p className="text-sm text-muted-foreground mt-1">Search your network and send bulk RFI/RFQ emails, grouped by segment.</p>
          </div>
          <Button onClick={() => { if (!selected.length) { toast.error("Select at least one contact"); return; } setComposing(true); }}
            disabled={!selected.length} data-testid="contacts-email-btn" className="rounded-sm">
            <Mail className="w-4 h-4 mr-2" /> Email {selected.length > 0 ? `(${selected.length})` : ""}
          </Button>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          <button onClick={() => setSegment("all")} data-testid="contacts-stat-total"
            className={`text-left p-4 rounded-sm border transition-colors ${segment === "all" ? "border-signal-500 bg-signal-500/10" : "border-border hover:bg-muted/40"}`}>
            <div className="text-2xl font-bold">{facets.total}</div>
            <div className="text-[0.7rem] uppercase tracking-wider text-muted-foreground">Total</div>
          </button>
          {facets.segments.map((s, i) => (
            <button key={s.key} onClick={() => setSegment(s.key)} data-testid={`contacts-stat-${s.key}`}
              className={`text-left p-4 rounded-sm border transition-colors ${segment === s.key ? "border-signal-500 bg-signal-500/10" : "border-border hover:bg-muted/40"}`}>
              <div className="text-2xl font-bold flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${segColor(i)}`} />{s.count}</div>
              <div className="text-[0.7rem] uppercase tracking-wider text-muted-foreground truncate">{prettySeg(s.key)}</div>
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="p-4 rounded-sm border border-border space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="contacts-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Company, contact, country, email…" className="pl-9" />
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <select value={segment} onChange={(e) => setSegment(e.target.value)} data-testid="contacts-segment-filter"
              className="h-9 rounded-sm bg-background border border-border text-sm px-2">
              <option value="all">All segments</option>
              {facets.segments.map((s) => <option key={s.key} value={s.key}>{prettySeg(s.key)}</option>)}
            </select>
            <select value={country} onChange={(e) => setCountry(e.target.value)} data-testid="contacts-country-filter"
              className="h-9 rounded-sm bg-background border border-border text-sm px-2 max-w-[220px]">
              <option value="all">All countries</option>
              {facets.countries.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <button onClick={clearFilters} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="contacts-clear">
              <RotateCcw className="w-3 h-3" /> Clear
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span data-testid="contacts-count">Showing {items.length} of {total} contacts</span>
          {selected.length > 0 && <button onClick={() => setSelected([])} className="hover:text-foreground" data-testid="contacts-clear-selection">Clear selection ({selected.length})</button>}
        </div>
        <div className="border border-border rounded-sm overflow-hidden">
          <div className="grid grid-cols-12 px-4 py-3 bg-muted/40 text-[0.7rem] uppercase tracking-wider text-muted-foreground font-semibold">
            <div className="col-span-1"><input type="checkbox" checked={allOnPage} onChange={toggleAll} className="accent-signal-500 cursor-pointer" data-testid="contacts-select-all" /></div>
            <div className="col-span-4">Company</div>
            <div className="col-span-2">Segment</div>
            <div className="col-span-2">Country</div>
            <div className="col-span-3">Contact</div>
          </div>
          {loading && items.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">No contacts match your filters.</div>
          ) : items.map((p, i) => (
            <div key={p.id} className={`grid grid-cols-12 px-4 py-3 items-center border-t border-border text-sm hover:bg-muted/30 ${selected.includes(p.id) ? "bg-signal-500/5" : ""}`} data-testid={`contact-row-${i}`}>
              <div className="col-span-1"><input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggle(p.id)} className="accent-signal-500 cursor-pointer" data-testid={`contact-select-${i}`} /></div>
              <div className="col-span-4 min-w-0">
                <div className="font-medium truncate flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-muted-foreground shrink-0" />{p.company || "—"}</div>
                {p.title && <div className="text-xs text-muted-foreground truncate">{p.title}</div>}
              </div>
              <div className="col-span-2"><span className="text-[0.6rem] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-muted border border-border">{prettySeg(p.segment)}</span></div>
              <div className="col-span-2 text-muted-foreground truncate inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{(p.location || p.country || "—").split(",")[0]}</div>
              <div className="col-span-3 min-w-0">
                <div className="truncate">{p.full_name || "—"}</div>
                <div className="text-xs text-signal-500 truncate">{p.email}</div>
              </div>
            </div>
          ))}
        </div>
        {hasMore && <div className="text-center"><Button variant="outline" size="sm" onClick={() => load(items.length)} data-testid="contacts-load-more">Load more</Button></div>}
      </div>

      {composing && (
        <BulkEmailModal selected={selected} contacts={items} onClose={() => setComposing(false)}
          onSent={() => { setComposing(false); setSelected([]); }} />
      )}
    </DashboardLayout>
  );
}

function BulkEmailModal({ selected, contacts, onClose, onSent }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [cc, setCc] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  // Group the selected contacts by segment for the preview.
  const chosen = contacts.filter((c) => selected.includes(c.id));
  const groups = {};
  chosen.forEach((c) => { const k = (c.segment || "GENERAL").replace(/_/g, " ").toUpperCase(); groups[k] = (groups[k] || 0) + 1; });

  const addFiles = async (list) => {
    const out = [];
    for (const f of Array.from(list)) {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} is over 10 MB`); continue; }
      const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res((r.result || "").toString().split(",")[1]); r.readAsDataURL(f); });
      out.push({ name: f.name, content_base64: b64, content_type: f.type || "application/octet-stream", size: f.size });
    }
    setFiles((prev) => [...prev, ...out]);
  };

  const send = async () => {
    if (!subject.trim() || !message.trim()) { toast.error("Add a subject and message"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/contacts/bulk-email", {
        prospect_ids: selected, subject, message,
        cc: cc.split(",").map((c) => c.trim()).filter(Boolean),
        attachments: files.map((f) => ({ name: f.name, content_base64: f.content_base64, content_type: f.content_type })),
      });
      if (data.ok) { toast.success(`Sent to ${data.sent} contact${data.sent === 1 ? "" : "s"} across ${data.groups.length} segment${data.groups.length === 1 ? "" : "s"}`); onSent(); }
      else { toast.error(`Send failed — ${(data.groups?.[0]?.error) || "check M365 connection"}`); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-start justify-center p-4 pt-8 overflow-y-auto" onClick={() => !sending && onClose()} data-testid="contacts-email-modal">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="text-sm font-semibold inline-flex items-center gap-2"><Mail className="w-4 h-4 text-signal-500" /> Send Bulk Email (RFI/RFQ)</div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="contacts-email-close"><X className="w-4 h-4" /></Button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-muted-foreground">Sending to <b className="text-foreground">{selected.length}</b> contacts via BCC. Emails are sent separately per segment with a <b>[SEGMENT]</b> tag.</p>
          <div className="flex flex-wrap gap-2" data-testid="contacts-email-groups">
            {Object.entries(groups).map(([k, n], i) => (
              <span key={k} className={`text-[0.7rem] px-2 py-1 rounded-sm text-white ${segColor(i)}`}>[{k}] → {n}</span>
            ))}
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Subject <span className="opacity-60">— [SEGMENT] is added automatically</span></label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. RFQ - Sea Freight from Shanghai to Koper" className="mt-1" data-testid="contacts-email-subject" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Message</label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={8} placeholder="Dear Partner, we are looking for rates for the following shipment…" className="mt-1 text-sm" data-testid="contacts-email-message" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">CC (optional) <span className="opacity-60">— comma separated</span></label>
            <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="colleague@europacific.com, manager@europacific.com" className="mt-1" data-testid="contacts-email-cc" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Attachments (optional) <span className="opacity-60">— max 10 MB/file</span></label>
            <div onClick={() => fileRef.current?.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
              className="mt-1 border border-dashed border-border rounded-sm p-4 text-center text-sm text-muted-foreground cursor-pointer hover:bg-muted/30" data-testid="contacts-email-drop">
              <Paperclip className="w-4 h-4 inline mr-1" /> Click to choose files or drag &amp; drop
              <input ref={fileRef} type="file" multiple hidden onChange={(e) => addFiles(e.target.files)} />
            </div>
            {files.length > 0 && (
              <div className="mt-2 space-y-1">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs bg-muted/40 rounded-sm px-2 py-1">
                    <Paperclip className="w-3 h-3" /><span className="truncate flex-1">{f.name}</span>
                    <span className="text-muted-foreground">{(f.size / 1024).toFixed(0)} KB</span>
                    <button onClick={() => setFiles(files.filter((_, x) => x !== i))} className="text-muted-foreground hover:text-red-400"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-border">
          <Button variant="ghost" onClick={onClose} disabled={sending} data-testid="contacts-email-cancel">Cancel</Button>
          <Button onClick={send} disabled={sending} data-testid="contacts-email-send">
            {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-2" /> Send Email</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
